import { apiClient } from "@/lib/apiClient";
import { formatDateLong } from "@/utils";
import { getDeliveryTypeLabel } from "@/features/education-catalog/utils";
import type { ClientEducationRequest, ClientEducationRequestStatus } from "../types/client-education-requests.types";

const EDUCATION_ENROLLMENTS_ME_ENDPOINT = "/api/client/education-enrollments/me";
const DEFAULT_CARD_IMAGE = "https://picsum.photos/seed/edu/400/200";

/** Backend enrollment status (EnrollmentStatus enum — euas-backend). */
export const ENROLLMENT_STATUS = {
  REQUESTED: 0,
  APPROVED_BRANCH_MANAGER: 1,
  ACTIVE: 2,
  REJECTED_BY_BRANCH_MANAGER: 3,
  REJECTED_BY_CENTRAL_MANAGER: 4,
  CANCELLED: 5,
  CONTENT_COMPLETED: 6,
  EXAM_FAILED: 7,
  EXAM_COMPLETED: 8,
  COMPLETED: 9,
  NOT_COMPLETED: 11,
  APPROVED_BY_EDUCATION_SUPERVISOR: 15,
  REJECTED_BY_EDUCATION_SUPERVISOR: 25,
  EXAM_STARTED: 65,
  SURVEY_STARTED: 82,
} as const;

/** Backend response item */
export interface MyEducationEnrollmentItem {
  id: string;
  educationID: string;
  userID: string;
  status: number;
  /** Eski alan — yeni sürüm `requestDate` kullanıyor, ikisini de destekliyoruz */
  requestedAt?: string;
  /** Talep tarihi (yeni BE alanı) */
  requestDate?: string;
  reviewedAt?: string | null;
  progressPercent?: number;
  education?: {
    id?: string;
    name?: string;
    title?: string;
    categories?: Array<{ id?: string; name?: string; label?: string }>;
    instructors?: Array<{ firstName?: string; lastName?: string; name?: string }>;
    coverImageFileID?: string | null;
    coverImageUrl?: string | null;
    coverImage?: string;
    image?: string;
    type?: number;
    format?: string;
    deliveryType?: string;
    category?: string;
    categoryName?: string;
    durationHours?: number;
    durationMinutes?: number;
    requestCollectionStartDate?: string | null;
    requestCollectionEndDate?: string | null;
    /** Eğitim başlangıç tarihi — belirlendiyse dolu */
    startDate?: string | null;
    /** Eğitim bitiş tarihi — belirlendiyse dolu */
    endDate?: string | null;
    /** BE tarafından hesaplanmış: tarih belirlendi mi? (startDate && endDate) */
    hasScheduledDates?: boolean;
    /** Eğitim lokasyon adresi (ON_SITE için) */
    location?: string | null;
    /** Eğitim oda / sınıf adı — tarihi belirlenmiş eğitimlerde gösterilir */
    roomName?: string | null;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

export interface MyEducationEnrollmentsResponse {
  items?: MyEducationEnrollmentItem[];
  data?: MyEducationEnrollmentItem[];
  count?: number;
  total?: number;
}

function statusToDisplayStatus(status: number): ClientEducationRequestStatus {
  switch (status) {
    case ENROLLMENT_STATUS.ACTIVE:
    case ENROLLMENT_STATUS.CONTENT_COMPLETED:
    case ENROLLMENT_STATUS.EXAM_FAILED:
    case ENROLLMENT_STATUS.EXAM_COMPLETED:
    case ENROLLMENT_STATUS.EXAM_STARTED:
    case ENROLLMENT_STATUS.SURVEY_STARTED:
    case ENROLLMENT_STATUS.COMPLETED:
    case ENROLLMENT_STATUS.NOT_COMPLETED:
      return "approved";
    case ENROLLMENT_STATUS.REJECTED_BY_BRANCH_MANAGER:
    case ENROLLMENT_STATUS.REJECTED_BY_CENTRAL_MANAGER:
    case ENROLLMENT_STATUS.REJECTED_BY_EDUCATION_SUPERVISOR:
    case ENROLLMENT_STATUS.CANCELLED:
      return "rejected";
    case ENROLLMENT_STATUS.REQUESTED:
    case ENROLLMENT_STATUS.APPROVED_BRANCH_MANAGER:
    case ENROLLMENT_STATUS.APPROVED_BY_EDUCATION_SUPERVISOR:
    default:
      return "pending";
  }
}

function mapToClientEducationRequest(
  item: MyEducationEnrollmentItem
): ClientEducationRequest {
  const edu = item.education ?? {};
  const title = edu.name ?? edu.title ?? "-";
  const category = (() => {
    const cats = edu.categories;
    if (cats?.length) return cats[0]?.name ?? cats[0]?.label ?? "-";
    return edu.category ?? edu.categoryName ?? "-";
  })();
  const deliveryTypeLabel = getDeliveryTypeLabel(edu.type, edu.format);
  const format =
    deliveryTypeLabel !== "-"
      ? deliveryTypeLabel
      : edu.deliveryType ?? edu.format ?? "-";
  // BE yeni sürümde `requestDate` döndürüyor; eski `requestedAt` için fallback
  const requestDate = formatDateLong(item.requestDate ?? item.requestedAt ?? "");

  const imageUrl = edu.coverImageUrl ?? edu.coverImage ?? edu.image ?? DEFAULT_CARD_IMAGE
  // BE tarafından hesaplanmış `hasScheduledDates` tercih edilir; yoksa local fallback
  const hasScheduledDates =
    typeof edu.hasScheduledDates === "boolean"
      ? edu.hasScheduledDates
      : Boolean(edu.startDate) && Boolean(edu.endDate);
  return {
    id: item.id,
    educationId: item.educationID ?? edu.id ?? item.id,
    imageFileId: imageUrl !== DEFAULT_CARD_IMAGE ? null : (edu.coverImageFileID ?? null),
    fallbackImageUrl: imageUrl,
    title,
    format,
    category,
    requestDate,
    status: statusToDisplayStatus(item.status),
    enrollmentStatus: item.status,
    hasScheduledDates,
    requestCollectionStartDate: edu.requestCollectionStartDate ?? null,
    requestCollectionEndDate: edu.requestCollectionEndDate ?? null,
  };
}

export interface GetMyEnrollmentsParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  type?: number;
}

export const clientEducationRequestsApi = {
  /**
   * Kullanıcının eğitim taleplerini listele - GET /api/client/education-enrollments/me
   * page, limit ve filtreler API'ye gönderilir.
   */
  getMyEnrollments: async (
    params?: GetMyEnrollmentsParams
  ): Promise<{ data: ClientEducationRequest[]; total: number }> => {
    const page = params?.page ?? 1;
    const limit = Math.min(100, Math.max(1, params?.limit ?? 20));
    const requestParams: Record<string, string | number> = {
      page,
      limit,
    };
    if (params?.search?.trim()) requestParams.search = params.search.trim();
    if (params?.status !== undefined && params.status !== null)
      requestParams.status = params.status;
    if (params?.sortBy) requestParams.sortBy = params.sortBy;
    if (params?.sortOrder) requestParams.sortOrder = params.sortOrder;
    if (params?.type !== undefined && params.type !== null)
      requestParams.type = params.type;

    const response = await apiClient.get<MyEducationEnrollmentsResponse>(
      EDUCATION_ENROLLMENTS_ME_ENDPOINT,
      { params: requestParams }
    );
    const resData = response.data ?? {};
    const rawItems =
      resData?.items ?? resData?.data ?? (Array.isArray(response.data) ? response.data : []);
    const items = Array.isArray(rawItems) ? rawItems : [];
    const total = resData?.count ?? resData?.total ?? items.length;

    return {
      data: items.map((e: MyEducationEnrollmentItem) =>
        mapToClientEducationRequest(e)
      ),
      total: typeof total === "number" ? total : items.length,
    };
  },
};
